import React from "react";
import PropTypes from "prop-types";
import { Button, Select, XClose } from "@opendate-app/setlist";
import AddWindowPicker from "../../ConfirmTicketing/components/AddWindowPicker";
import { PUBLIC_ON_SALE_NAME, windowNameFor } from "../../ConfirmTicketing/utils/presaleWindows";
import "../../ConfirmTicketing/components/SalesWindows.css";
import { blankPresaleWindow, PROMO_CODE_NAME_FORMAT } from "../scheduleBuilder";
import ShowDetailOffset, { offsetsWithinAYear } from "./ShowDetailOffset";

const EMAIL_CODE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const presaleWindowProblems = (windows, { ticketName, takenNames = [], inventory }) => {
  const names = windows.map((window) => (window.name || "").trim());
  const pool = inventory === "" || inventory == null ? null : Number(inventory);
  return Object.fromEntries(
    windows.map((window, index) => {
      const name = names[index];
      const code = (window.accessCode || "").trim().toUpperCase();
      let problem = null;
      if (!name) problem = "Give this window a name.";
      else if (name === ticketName.trim() || takenNames.includes(name) || names.indexOf(name) !== index)
        problem = "Another ticket or window already uses this name.";
      else if (!window.startActivity || !window.endActivity) problem = "Choose when this window opens and closes.";
      else if (!offsetsWithinAYear(window.startOffsetMinutes, window.endOffsetMinutes))
        problem = "Open and close within a year of the show details.";
      else if (code && !EMAIL_CODE.test(code) && !PROMO_CODE_NAME_FORMAT.test(code))
        problem = "Access codes use letters, numbers, - _ % ! or an email address.";
      else if (pool != null && window.inventory !== "" && Number(window.inventory) > pool)
        problem = `This window holds more than the ${pool} tickets this ticket has.`;
      return [window.id, problem];
    }),
  );
};

const PresaleWindowsSection = ({ windows, ticketName, showActivityNames, unsupported, free, problems, onChange }) => {
  const activityOptions = showActivityNames.map((name) => ({ value: name, label: name }));
  const update = (id, key, value, extra = {}) =>
    onChange(windows.map((window) => (window.id === id ? { ...window, [key]: value, ...extra } : window)));

  const addWindow = (preset) =>
    onChange([
      ...windows,
      blankPresaleWindow(
        preset
          ? { name: windowNameFor(ticketName.trim(), preset.label), startActivity: preset.startName, endActivity: PUBLIC_ON_SALE_NAME }
          : {},
      ),
    ]);

  return (
    <>
      <h3 className="series-drawer-section-title">Pre-sale windows</h3>
      <p className="series-edit-hint">
        {unsupported
          ? "Pre-sale windows are not supported on seated or timed entry series yet. Remove them to publish."
          : "Sell this ticket early, at its own price or quantity, before the public on-sale. A window with an access code stays hidden until a fan enters it."}
      </p>
      {windows.map((window) => (
        <div key={window.id} className="series-plan-subrow" data-testid="series-presale-window">
          <div className="series-plan-subrow-header">
            <input
              aria-label="Window name"
              className="series-edit-input"
              placeholder="Window name"
              value={window.name}
              onChange={(event) => update(window.id, "name", event.target.value)}
            />
            <Button
              variant="tertiary"
              size="sm"
              icon={XClose}
              aria-label={`Remove ${window.name || "window"}`}
              onClick={() => onChange(windows.filter((candidate) => candidate.id !== window.id))}
            />
          </div>
          <div className="series-edit-row">
            <Select
              label="Opens"
              placeholder="Select a show detail"
              options={activityOptions}
              value={window.startActivity}
              disabled={unsupported}
              onChange={(value) => update(window.id, "startActivity", value || "", { startOffsetMinutes: 0 })}
            />
            <Select
              label="Closes"
              placeholder="Select a show detail"
              options={activityOptions}
              value={window.endActivity}
              disabled={unsupported}
              onChange={(value) => update(window.id, "endActivity", value || "", { endOffsetMinutes: 0 })}
            />
          </div>
          {!unsupported && window.startActivity && (
            <ShowDetailOffset
              label={`${window.name || "Window"} opens`}
              anchorName={window.startActivity}
              minutes={window.startOffsetMinutes}
              onChange={(minutes) => update(window.id, "startOffsetMinutes", minutes)}
            />
          )}
          {!unsupported && window.endActivity && (
            <ShowDetailOffset
              label={`${window.name || "Window"} closes`}
              anchorName={window.endActivity}
              minutes={window.endOffsetMinutes}
              onChange={(minutes) => update(window.id, "endOffsetMinutes", minutes)}
            />
          )}
          <div className="series-edit-row">
            <label className="series-edit-label">
              Price
              <input
                type="number"
                min="0"
                step="0.01"
                className="series-edit-input"
                placeholder={free ? "Free" : "Same as ticket"}
                value={free ? "" : window.price}
                disabled={unsupported || free}
                onChange={(event) => update(window.id, "price", event.target.value)}
              />
            </label>
            <label className="series-edit-label">
              Quantity
              <input
                type="number"
                min="0"
                className="series-edit-input"
                placeholder="Shares the ticket's pool"
                value={window.inventory}
                disabled={unsupported}
                onChange={(event) => update(window.id, "inventory", event.target.value)}
              />
            </label>
          </div>
          <label className="series-edit-label">
            Access code
            <input
              className="series-edit-input"
              placeholder="Optional"
              value={window.accessCode}
              disabled={unsupported}
              onChange={(event) => update(window.id, "accessCode", event.target.value.toUpperCase())}
            />
          </label>
          {problems[window.id] && <p className="series-schedule-error">{problems[window.id]}</p>}
        </div>
      ))}
      <AddWindowPicker
        showActivities={showActivityNames.map((name) => ({ name }))}
        disabled={unsupported || activityOptions.length === 0}
        onPick={addWindow}
      />
    </>
  );
};

PresaleWindowsSection.propTypes = {
  windows: PropTypes.arrayOf(PropTypes.object).isRequired,
  ticketName: PropTypes.string.isRequired,
  showActivityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  unsupported: PropTypes.bool,
  free: PropTypes.bool,
  problems: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

PresaleWindowsSection.defaultProps = { unsupported: false, free: false };

export default PresaleWindowsSection;
